import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Save, CheckCircle2 } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import AiPromptConfigService from '../../services/aiPromptConfigService';
import AiRfpService from '../../services/aiRfpService';
import { DEFAULT_SECTION_KEYS, humanizeSectionKey } from '../../utils/rfpGeneratedContentUtils';
import { resolveSectionDefinitions } from '../../utils/aiPromptConfigUtils';
import api from '../../services/api';

const INITIAL_FORM = {
  projectName: '',
  projectType: '',
  department: '',
  location: '',
  scope: '',
  budget: '',
  timeline: '',
  templateSelection: '',
};

const INITIAL_PROGRESS = {
  isActive: false,
  totalSections: 0,
  completedSections: 0,
  currentSectionKey: '',
  currentSectionLabel: '',
  percent: 0,
  status: 'idle',
  sectionStatuses: [],
};

const AiRfpInput = () => {
  const navigate = useNavigate();
  const { error: showError, success, toasts, removeToast } = useToast();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [activePrompt, setActivePrompt] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(INITIAL_PROGRESS);

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  const sectionPlan = useMemo(() => {
    const defs = resolveSectionDefinitions(activePrompt);
    if (defs.length) return defs;
    return DEFAULT_SECTION_KEYS.map((key, index) => ({
      key,
      label: humanizeSectionKey(key),
      order: index + 1,
      builderSlot: 'document_overflow',
    }));
  }, [activePrompt]);

  useEffect(() => {
    const loadActivePrompt = async () => {
      try {
        const prompt = await AiPromptConfigService.getActivePrompt();
        setActivePrompt(prompt || null);
      } catch {
        setActivePrompt(null);
      }
    };

    loadActivePrompt();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.get('/sourcing/pre-rft/projects');
        const projectList = res.data?.projects || [];
        setProjects(projectList);
      } catch {
        setProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  const templateOptions = useMemo(
    () => [
      'General Procurement',
      'IT Services',
      'Construction Works',
      'Facility Management',
      'Professional Services',
    ],
    [],
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProjectSelect = (projectId) => {
    setSelectedProjectId(projectId);
    if (!projectId) {
      setSelectedProject(null);
      setFormData(INITIAL_FORM);
      return;
    }
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;

    setSelectedProject(project);
    setFormData({
      projectName: project.title || '',
      projectType: project.procurementApproval?.procurementStrategy || '',
      department: project.adminApproval?.approvalAuthority || '',
      location: project.dprPreparation?.dprScope || '',
      scope: project.description || project.identificationOfNeed?.proposedSolution || '',
      budget: project.budgetInclusion?.budgetAllocation
        ? String(project.budgetInclusion.budgetAllocation)
        : '',
      timeline: project.dprPreparation?.implementationTimeline || '',
      templateSelection: formData.templateSelection,
    });
  };

  const buildInputPayload = () => {
    const payload = { ...formData };
    if (selectedProject) {
      payload.projectContext = selectedProject;
    }
    return payload;
  };

  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      const response = await AiRfpService.createDraft(buildInputPayload(), activePrompt);
      const createdDraftId = response?.data?._id;
      if (createdDraftId) {
        setDraftId(createdDraftId);
      }
      success('AI RFP draft saved successfully.');
    } catch (err) {
      showError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to save AI RFP draft.',
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleGenerate = async () => {
    const payload = buildInputPayload();
    const totalSections = sectionPlan.length;

    try {
      setIsGenerating(true);
      setGenerationProgress({
        isActive: true,
        totalSections,
        completedSections: 0,
        currentSectionKey: sectionPlan[0]?.key || '',
        currentSectionLabel: sectionPlan[0]?.label || humanizeSectionKey(sectionPlan[0]?.key || ''),
        percent: 0,
        status: 'generating',
        sectionStatuses: sectionPlan.map((section) => ({
          key: section.key,
          label: section.label || humanizeSectionKey(section.key),
          status: 'pending',
        })),
      });

      let workingDraftId = draftId;
      if (!workingDraftId) {
        const draftResponse = await AiRfpService.createDraft(payload, activePrompt);
        workingDraftId = draftResponse?.data?._id || null;
        if (workingDraftId) setDraftId(workingDraftId);
      }

      const result = await AiRfpService.generateRfpBySections({
        draftId: workingDraftId,
        inputPayload: payload,
        promptConfig: activePrompt,
        onProgress: (progress) => {
          setGenerationProgress((prev) => ({
            ...prev,
            isActive: true,
            totalSections: progress.totalSections,
            completedSections: progress.completedSections,
            currentSectionKey: progress.currentSectionKey,
            currentSectionLabel: progress.currentSectionLabel,
            percent: progress.percent,
            status: progress.status,
            sectionStatuses: sectionPlan.map((section, index) => {
              const key = section.key;
              if (index < progress.completedSections) {
                return { key, label: section.label || humanizeSectionKey(key), status: 'done' };
              }
              if (key === progress.currentSectionKey && progress.status === 'generating') {
                return { key, label: section.label || humanizeSectionKey(key), status: 'active' };
              }
              if (
                progress.failedSections?.includes(key) &&
                progress.status === 'section_failed'
              ) {
                return { key, label: section.label || humanizeSectionKey(key), status: 'failed' };
              }
              return { key, label: section.label || humanizeSectionKey(key), status: 'pending' };
            }),
          }));
        },
      });

      if (result.draftId) setDraftId(result.draftId);

      success('AI RFP generated successfully.');
      navigate('/sourcing/rfps/create/workspace', {
        state: {
          source: 'ai-generated',
          generatedContent: result.generatedContent,
          inputPayload: payload,
          promptConfigInjection: activePrompt || null,
          draftId: result.draftId || workingDraftId || null,
          sectionOrder: result.sectionOrder,
        },
      });
    } catch (err) {
      showError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to generate AI RFP draft.',
      );
    } finally {
      setIsGenerating(false);
      setGenerationProgress(INITIAL_PROGRESS);
    }
  };

  const hasAnyInput = !!selectedProjectId || Object.values(formData).some(
    (value) => String(value).trim().length > 0,
  );

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="AI RFP Input"
        description="Select a Pre-RFP project and template to generate an AI-assisted RFP draft."
      />

      <div className="page-container">
        <div
          className="card"
          style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}
        >
          <div className="card-body" style={{ padding: '20px' }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <Sparkles size={16} />
              <h6 className="mb-0">Form Card</h6>
            </div>

            <div className="row g-3">
              <div className="col-lg-12">
                <label className="form-label">Select Pre-RFP Project</label>
                <select
                  className="form-select"
                  value={selectedProjectId}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  disabled={isLoadingProjects || isGenerating}
                >
                  <option value="">
                    {isLoadingProjects ? 'Loading projects...' : 'Select a Pre-RFP project'}
                  </option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.title}
                    </option>
                  ))}
                </select>
                {selectedProjectId && (
                  <small className="text-muted" style={{ display: 'block', marginTop: 4 }}>
                    Project fields (name, scope, budget, timeline, etc.) will be auto-populated from the selected Pre-RFP project.
                  </small>
                )}
              </div>

              <div className="col-lg-6">
                <label className="form-label">Template Selection</label>
                <select
                  className="form-select"
                  value={formData.templateSelection}
                  onChange={(e) =>
                    handleChange('templateSelection', e.target.value)
                  }
                  disabled={isGenerating}
                >
                  <option value="">Select template</option>
                  {templateOptions.map((template) => (
                    <option key={template} value={template}>
                      {template}
                    </option>
                  ))}
                </select>
              </div>

              {activePrompt?.promptName && (
                <div className="col-lg-12">
                  <small className="text-muted">
                    Active AI prompt: <strong>{activePrompt.promptName}</strong>
                    {' '}({sectionPlan.length} sections)
                  </small>
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb',
                  margin: '0 -24px -24px -24px',
                  borderRadius: '0 0 8px 8px',
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate('/sourcing/rfps')}
                  disabled={isGenerating}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: isGenerating ? 'not-allowed' : 'pointer',
                    opacity: isGenerating ? 0.7 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={!hasAnyInput || isSavingDraft || isGenerating}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: (!hasAnyInput || isSavingDraft || isGenerating) ? 'not-allowed' : 'pointer',
                    opacity: (!hasAnyInput || isSavingDraft || isGenerating) ? 0.7 : 1,
                  }}
                >
                  {isSavingDraft ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isGenerating || isSavingDraft || !hasAnyInput}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (isGenerating || isSavingDraft || !hasAnyInput) ? 'not-allowed' : 'pointer',
                    opacity: (isGenerating || isSavingDraft || !hasAnyInput) ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Save size={18} />
                  {isGenerating ? 'Generating...' : 'Generate RFP'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {generationProgress.isActive && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(2px)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: '560px',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            }}
          >
            <div className="card-body" style={{ padding: '24px' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Sparkles size={18} color="var(--primary-color)" />
                <h5 className="mb-0">Generating RFP</h5>
              </div>
              <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
                Government RFPs are generated section by section for accuracy. This may take a few minutes.
              </p>

              <div className="d-flex justify-content-between text-sm mb-2" style={{ fontSize: '13px' }}>
                <span>
                  {generationProgress.completedSections} of {generationProgress.totalSections} sections
                </span>
                <span>{generationProgress.percent}%</span>
              </div>

              <div
                className="mb-3"
                style={{
                  height: '10px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '999px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${generationProgress.percent}%`,
                    backgroundColor: 'var(--primary-color)',
                    transition: 'width 0.35s ease',
                  }}
                />
              </div>

              <p className="mb-3" style={{ fontSize: '14px', fontWeight: 500 }}>
                {generationProgress.status === 'complete'
                  ? 'Finalizing...'
                  : `Generating: ${generationProgress.currentSectionLabel}`}
              </p>

              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                {generationProgress.sectionStatuses.map((section) => (
                  <div
                    key={section.key}
                    className="d-flex align-items-center justify-content-between py-2 border-bottom"
                    style={{ fontSize: '13px' }}
                  >
                    <span>{section.label}</span>
                    <span
                      style={{
                        color:
                          section.status === 'done'
                            ? '#16a34a'
                            : section.status === 'active'
                              ? 'var(--primary-color)'
                              : section.status === 'failed'
                                ? '#dc2626'
                                : '#9ca3af',
                        fontWeight: section.status === 'active' ? 600 : 500,
                      }}
                    >
                      {section.status === 'done' && (
                        <span className="d-inline-flex align-items-center gap-1">
                          <CheckCircle2 size={14} />
                          Done
                        </span>
                      )}
                      {section.status === 'active' && 'In progress...'}
                      {section.status === 'pending' && 'Pending'}
                      {section.status === 'failed' && 'Failed'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default AiRfpInput;
